import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiCheck, FiChevronDown, FiHeart } from "react-icons/fi";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import { ProductCard } from "./Shop";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/productdetails.scss";

const colorMap = {
  navy: "#263348",
  blue: "#8fa9bd",
  slate: "#687b86",
  sand: "#c8b79c",
  ecru: "#e8e1d1",
  cream: "#e9e2d3",
  ivory: "#eee9dc",
  olive: "#62644a",
  green: "#485847",
  camel: "#b29069",
  brown: "#70513c",
  cognac: "#9c6038",
  tobacco: "#9b6846",
  charcoal: "#484744",
  black: "#222220",
  burgundy: "#6e3440",
  terracotta: "#ad6b56",
  white: "#f5f4ef",
};

const getSwatchColor = (name) => {
  const key = Object.keys(colorMap).find((color) => name.toLowerCase().includes(color));
  return key ? colorMap[key] : "#a49b8e";
};

function ProductDetail() {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const [sizeError, setSizeError] = useState(false);

  useEffect(() => {
    if (!product) return;
    setSelectedSize("");
    setSelectedColor(product.colors[0]);
    setSelectedImage(0);
    setImageLoading(true);
    setAdded(false);
    setSizeError(false);
  }, [product]);

  const recommendations = useMemo(() => {
    if (!product) return [];
    const related = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    return [...related, ...productData.filter((item) => item.id !== product.id && item.category !== product.category)].slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-not-found">
        <p className="eyebrow">404</p>
        <h1>This piece could not be found.</h1>
        <Link className="button button--dark" to="/shop">
          Return to the collection
        </Link>
      </main>
    );
  }

  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 4);
  const wished = isWishlisted(product.id);

  const addProduct = () => {
    if (!selectedSize) {
      setSizeError(true);
      return;
    }
    addToCart(product.id, 1, selectedSize, selectedColor);
    setAdded(true);
    setSizeError(false);
  };

  return (
    <main className="product-detail">
      <nav className="product-breadcrumb" aria-label="Breadcrumb">
        <Link to="/shop">Collection</Link>
        <span>/</span>
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>{product.category}</Link>
        <span>/</span>
        <span>{product.name}</span>
      </nav>

      <div className="product-detail__layout">
        <section className="product-gallery" aria-label={`${product.name} gallery`}>
          <div className={`product-gallery__main ${imageLoading ? "is-loading" : ""}`}>
            <img
              src={product.images[selectedImage]}
              alt={`${product.name} in ${selectedColor}`}
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
            />
          </div>
          {product.images.length > 1 && (
            <div className="product-gallery__thumbs">
              {product.images.map((image, index) => (
                <button
                  type="button"
                  key={image}
                  className={selectedImage === index ? "is-active" : ""}
                  onClick={() => {
                    setSelectedImage(index);
                    setImageLoading(true);
                  }}
                  aria-label={`View image ${index + 1}`}
                >
                  <img src={image} alt="" />
                </button>
              ))}
            </div>
          )}
        </section>

        <aside className="product-info">
          <div className="product-info__heading">
            <p>{product.category}</p>
            <h1>{product.name}</h1>
            <span>{formatPrice(product.price)}</span>
          </div>

          <p className="product-info__intro">{product.description}</p>

          <fieldset className="product-option product-option--colors">
            <legend>
              Color <span>— {selectedColor}</span>
            </legend>
            <div>
              {product.colors.map((color) => (
                <button
                  type="button"
                  key={color}
                  className={selectedColor === color ? "is-active" : ""}
                  aria-label={`Choose ${color}`}
                  onClick={() => setSelectedColor(color)}
                >
                  <span style={{ backgroundColor: getSwatchColor(color) }} />
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="product-option product-option--sizes">
            <legend>
              <span>Size</span>
              <button type="button">Size guide</button>
            </legend>
            <div>
              {product.sizes.map((size) => (
                <button
                  type="button"
                  key={size}
                  className={selectedSize === size ? "is-active" : ""}
                  onClick={() => {
                    setSelectedSize(size);
                    setSizeError(false);
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
            {sizeError && <p role="alert">Please choose your size.</p>}
          </fieldset>

          <div className="product-purchase">
            <button type="button" className="button button--dark" onClick={addProduct}>
              {added ? (
                <>
                  <FiCheck /> Added to bag
                </>
              ) : (
                `Add to bag — ${formatPrice(product.price)}`
              )}
            </button>
            <button
              type="button"
              className={`product-purchase__wishlist ${wished ? "is-active" : ""}`}
              aria-label={`${wished ? "Remove from" : "Add to"} wishlist`}
              onClick={() => toggleWishlist(product.id)}
            >
              <FiHeart />
            </button>
          </div>

          {added && (
            <p className="product-added-message" role="status">
              This piece is in your bag. <Link to="/cartpage">View bag</Link>
            </p>
          )}

          <div className="product-assurances">
            <span>Free delivery from {formatPrice(siteConfig.shipping.freeThreshold)}</span>
            <span>Free returns within {siteConfig.shipping.returnsWindow} days</span>
          </div>

          <div className="product-accordions">
            <details open>
              <summary>
                Details & composition <FiChevronDown />
              </summary>
              <p>{product.material} · {product.fit}</p>
              <ul>
                {product.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </details>
            <details>
              <summary>
                Care <FiChevronDown />
              </summary>
              <p>{product.attributes.care}. Store folded or on a well-shaped hanger.</p>
            </details>
            <details>
              <summary>
                Delivery & returns <FiChevronDown />
              </summary>
              <p>
                Estimated delivery from {deliveryDate.toLocaleDateString("en-US", { month: "long", day: "numeric" })}.
                Complimentary returns are accepted within {siteConfig.shipping.returnsWindow} days.
              </p>
            </details>
          </div>
        </aside>
      </div>

      <section className="product-recommendations section-pad">
        <div className="section-heading">
          <div>
            <p className="eyebrow">COMPLETE THE WARDROBE</p>
            <h2>You may also like</h2>
          </div>
          <Link className="text-link" to="/shop">View all</Link>
        </div>
        <div className="product-grid">
          {recommendations.map((item) => (
            <ProductCard key={item.id} product={item} />
          ))}
        </div>
      </section>
    </main>
  );
}

export default ProductDetail;
